import {addDays, compareAsc, isValid, subDays} from 'date-fns';
import Decimal from 'decimal.js';
import {
  Choice,
  CurrencyInput,
  FormControl,
  FormField,
  Label,
  NumberInput,
  openConfirmDialog,
  OptionType,
  Separator,
} from 'platform/components';
import {Grid, GridItem, Heading, HStack, Show, Text, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {head, isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetServiceContractsQuery} from '@dms/api/customer';
import {
  AftersalesPaymentMethod,
  Payment,
  PaymentFieldsState,
  PaymentUiCapabilities,
  usePutCheckoutSetTypeOfSaleMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {
  DEFAULT_FOREIGN_CURRENCY,
  EMPTY_PLACEHOLDER,
  handleApiError,
  PredefinedNotes,
  useBank,
  useCashRegister,
  useTenant,
} from '@dms/shared';

import {
  CurrencyCodeType,
  getSafely,
  Nullish,
  precisionCalculation,
  RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

import {useFormValueChange} from '../hooks/useFormValueChange';
import {CheckoutPaymentFormTypeV2} from '../types/CheckoutPaymentFormTypeV2';

const DEFAULT_DUE_DATE_BANK_TRANSFER = 14;
const MIN_DUE_DAYS = 0;
const MAX_DUE_DAYS = 3650;

interface CheckoutPaymentFormBodyV2Props extends RequiredTestIdProps {
  checkoutId: string;
  control: FormControl<CheckoutPaymentFormTypeV2>;
  customerId: string;
  formApi: UseFormReturn<CheckoutPaymentFormTypeV2>;
  uiCapabilities: PaymentUiCapabilities;
  payment: Payment;
  totalAmount?: number;
  onSaveChanges: (data: CheckoutPaymentFormTypeV2) => Promise<unknown> | undefined;
  isEditingDisabled?: boolean;
  isForeignCurrencyAllowed?: boolean;
}

export function CheckoutPaymentFormBodyV2(props: CheckoutPaymentFormBodyV2Props) {
  const {control, formApi} = props;
  const {fieldState} = props.uiCapabilities;

  const fieldEnabled = (key: keyof PaymentFieldsState) =>
    getSafely(fieldState, key)?.state === 'EDIT';
  const fieldVisible = (key: keyof PaymentFieldsState) => {
    const field = getSafely(fieldState, key);
    return isNotNil(field) && field?.state !== 'HIDDEN';
  };
  const fieldRequired = (key: keyof PaymentFieldsState) =>
    getSafely(fieldState, key)?.isRequired ?? false;

  const serviceContracts = useGetServiceContractsQuery({customerId: props.customerId});
  const {bankAccountOptions, primaryBankAccountOption} = useBank();
  const {cashRegisterOptions} = useCashRegister();
  const {tenantCurrency} = useTenant();

  const [putCheckoutSetTypeOfSale] = usePutCheckoutSetTypeOfSaleMutation();

  const issueDate = useWatch({control: formApi.control, name: 'issueDate'});
  const paymentMethod = useWatch({control: formApi.control, name: 'method'});
  const note = useWatch({control: formApi.control, name: 'note'});
  const exchangeRate = useWatch({control: formApi.control, name: 'exchangeRate'});
  const amount = useWatch({control: formApi.control, name: 'amount'});

  const validIssueDate = isNotNil(issueDate) && isValid(issueDate) ? issueDate : null;
  const minTaxableEventDate = validIssueDate ? subDays(validIssueDate, 15) : new Date();
  const maxTaxableEventDate = validIssueDate ? validIssueDate : new Date();

  const serviceContractOptions =
    serviceContracts.data?.map((serviceContract) => ({
      label: serviceContract.number,
      value: serviceContract.serviceContractId,
    })) ?? [];

  const percentage = props.totalAmount
    ? parseFloat(
        precisionCalculation
          .multiply(precisionCalculation.divide(amount ?? 0, props.totalAmount ?? 0), 100)
          .toFixed(2)
      )
    : 0;

  useFormValueChange(
    exchangeRate,
    props.payment.exchangeRate ? parseFloat(props.payment.exchangeRate ?? '0') : null,
    (value) => formApi.setValue('exchangeRate', value)
  );

  useFormValueChange(amount, props.payment.paymentAmount.amount, (value) =>
    formApi.setValue('amount', value)
  );

  useFormValueChange(paymentMethod, props.payment.method, (value) =>
    formApi.setValue('method', value)
  );

  const handleSaveChanges = () => {
    props.onSaveChanges(formApi.getValues());
  };

  const handlePercentageChange = (value: number | null) => {
    const newPercentage = parseFloat(
      precisionCalculation.multiply((value ?? 0) / 100, props.totalAmount ?? 0).toFixed(2)
    );

    formApi.setValue('amount', newPercentage);
    handleSaveChanges();
  };

  const afterPaymentChange = (newValue: AftersalesPaymentMethod) => {
    const issuedOn = formApi.getValues('issueDate');
    const validIssuedOn = isNotNil(issuedOn) && isValid(issuedOn) ? issuedOn : null;

    if (newValue === 'BANK_TRANSFER' && isNotNil(validIssuedOn)) {
      formApi.setValue('due', DEFAULT_DUE_DATE_BANK_TRANSFER, {shouldValidate: true});
      formApi.setValue('dueDate', addDays(validIssuedOn, DEFAULT_DUE_DATE_BANK_TRANSFER), {
        shouldValidate: true,
      });

      if (isNilOrEmpty(formApi.getValues('bankAccount'))) {
        formApi.setValue('bankAccount', primaryBankAccountOption?.value ?? '', {
          shouldValidate: true,
        });
      }

      return;
    }

    if (newValue === 'CASH' && isNilOrEmpty(formApi.getValues('cashRegisterId'))) {
      formApi.setValue('cashRegisterId', head(cashRegisterOptions)?.value ?? '', {
        shouldValidate: true,
      });
    }

    if (isNotNil(validIssuedOn)) {
      formApi.setValue('due', 0, {shouldValidate: true});
      formApi.setValue('dueDate', validIssuedOn, {shouldValidate: true});
    }
  };

  const handleSetPaymentMethod = (value: AftersalesPaymentMethod) => {
    formApi.setValue('method', value);
    afterPaymentChange(value);

    handleSaveChanges();
  };

  const handlePaymentMethodChange = (newValue: AftersalesPaymentMethod | Nullish) => {
    if (isNil(newValue)) {
      return;
    }

    if (newValue === 'INTERNAL') {
      openConfirmDialog({
        onConfirm: () =>
          putCheckoutSetTypeOfSale({
            checkoutId: props.checkoutId,
            body: {typeOfSale: 'INTERNAL'},
          })
            .unwrap()
            .then(() => handleSetPaymentMethod(newValue))
            .catch(handleApiError),
        text: i18n.t('entity.checkout.labels.freeOfChargeHelperText'),
      });
      return;
    }

    if (paymentMethod === 'INTERNAL') {
      openConfirmDialog({
        onConfirm: () =>
          putCheckoutSetTypeOfSale({
            checkoutId: props.checkoutId,
            body: {typeOfSale: 'DEFAULT'},
          })
            .unwrap()
            .then(() => handleSetPaymentMethod(newValue))
            .catch(handleApiError),
        text: i18n.t('entity.invoice.notifications.freeOfChargeChangeHelperText'),
      });
      return;
    }

    handleSetPaymentMethod(newValue);
  };

  const handleIssuedOnChange = (newIssueDate: Date | Nullish) => {
    if (isNil(newIssueDate) || !isValid(newIssueDate)) {
      return;
    }

    const due = formApi.getValues('due') ?? 0;
    const calculatedDueDate = addDays(newIssueDate, due);
    if (isValid(calculatedDueDate)) {
      formApi.setValue('dueDate', calculatedDueDate, {shouldValidate: true});
    }

    const taxableEventDate = formApi.getValues('dateOfTaxableSupply');

    const minDate = subDays(newIssueDate, 15);
    const maxDate = newIssueDate;

    if (isNil(taxableEventDate)) {
      return formApi.setValue('dateOfTaxableSupply', newIssueDate, {shouldValidate: true});
    }

    if (compareAsc(taxableEventDate, minDate) < 0) {
      return formApi.setValue('dateOfTaxableSupply', minDate, {shouldValidate: true});
    }

    if (compareAsc(taxableEventDate, maxDate) > 0) {
      return formApi.setValue('dateOfTaxableSupply', maxDate, {shouldValidate: true});
    }
  };

  const handleDueChange = (due: number | Nullish) => {
    if (isNil(due)) {
      return;
    }

    if (due < 0 || due > MAX_DUE_DAYS) {
      if (due < 0) {
        return formApi.setValue('due', 0, {shouldValidate: true});
      } else {
        return formApi.setValue('due', MAX_DUE_DAYS, {shouldValidate: true});
      }
    }

    const issuedOn = formApi.getValues('issueDate');
    const validIssuedOn = isNotNil(issuedOn) && isValid(issuedOn) ? issuedOn : null;

    if (isNotNil(validIssuedOn)) {
      const calculatedDueDate = addDays(validIssuedOn, due);
      if (isValid(calculatedDueDate)) {
        formApi.setValue('dueDate', calculatedDueDate, {shouldValidate: true});
      }
    }
  };

  const handleDueDateChange = (date: Date | Nullish) => {
    if (isNil(date) || !isValid(date)) {
      return;
    }

    const due = formApi.getValues('due') ?? 0;
    formApi.setValue('issueDate', subDays(date, due), {shouldValidate: true});
  };

  const allOrderPaymentOptions: Array<OptionType<AftersalesPaymentMethod>> = [
    {label: i18n.t('entity.invoice.paymentMethod.bankTransfer'), value: 'BANK_TRANSFER'},
    {label: i18n.t('entity.invoice.paymentMethod.card'), value: 'CARD'},
    {label: i18n.t('entity.invoice.paymentMethod.cash'), value: 'CASH'},
    {label: i18n.t('general.labels.internal'), value: 'INTERNAL'},
  ];
  const orderPaymentOptions = allOrderPaymentOptions.filter((option) =>
    props.payment.availablePaymentMethods.includes(option.value)
  );

  const foreignCurrencyAmount =
    exchangeRate && amount ? parseFloat(new Decimal(amount).div(exchangeRate).toFixed(2)) : 0;

  const isCorrectiveTaxDocument =
    props.payment.discriminator === 'BALANCE_CORRECTION_FULL' ||
    props.payment.discriminator === 'BALANCE_CORRECTION_PARTIAL';

  return (
    <VStack spacing={4}>
      <Grid spacing={4} columns={4}>
        <Show when={fieldVisible('paymentAmount')}>
          <FormField
            control={control}
            name="amount"
            type="currency"
            currency={tenantCurrency}
            label={i18n.t('general.labels.amount')}
            isDisabled={!fieldEnabled('paymentAmount') || props.isEditingDisabled}
            isRequired={fieldRequired('paymentAmount')}
            data-testid={suffixTestId('amount', props)}
          />
        </Show>
        <Show when={props.payment.discriminator === 'DEPOSIT' && fieldVisible('paymentAmount')}>
          <NumberInput
            suffix="%"
            value={percentage}
            onChange={handlePercentageChange}
            isDisabled={!fieldEnabled('paymentAmount') || props.isEditingDisabled}
            label={i18n.t('entity.order.labels.percentage')}
          />
        </Show>
        <Show when={props.isForeignCurrencyAllowed}>
          <FormField
            control={control}
            name="exchangeRate"
            type="currency"
            currency={tenantCurrency}
            label={i18n.t('general.labels.exchangeRate')}
            isRequired
            isDisabled={props.isEditingDisabled}
            data-testid={suffixTestId('exchangeRate', props)}
          />
          <CurrencyInput
            value={foreignCurrencyAmount}
            isDisabled
            label={i18n.t('entity.checkout.labels.amountInForeignCurrency')}
            currency={
              (props.payment.foreignCurrencyPaymentAmount?.currency ??
                DEFAULT_FOREIGN_CURRENCY) as CurrencyCodeType
            }
          />
        </Show>
      </Grid>
      <Grid spacing={4} columns={4}>
        <Show when={fieldVisible('issueDate')}>
          <FormField
            control={control}
            name="issueDate"
            type="date"
            isRequired={fieldRequired('issueDate')}
            isRelativeDatesHidden
            label={i18n.t('entity.checkout.labels.issuedOn')}
            onChange={handleIssuedOnChange}
            isDisabled={!fieldEnabled('issueDate') || props.isEditingDisabled}
            data-testid={suffixTestId('issueDate', props)}
            isNonClearable
          />
        </Show>
        <Show when={fieldVisible('due')}>
          <FormField
            control={control}
            name="due"
            type="number"
            label={i18n.t('entity.checkout.labels.due')}
            onChange={handleDueChange}
            isDisabled={!fieldEnabled('due') || props.isEditingDisabled}
            isRequired={fieldRequired('due')}
            data-testid={suffixTestId('due', props)}
            minStepperValue={MIN_DUE_DAYS}
            maxStepperValue={MAX_DUE_DAYS}
            isStepperVisible
          />
        </Show>
        <Show when={fieldVisible('dueDate')}>
          <FormField
            control={control}
            name="dueDate"
            type="date"
            isRelativeDatesHidden
            label={i18n.t('entity.checkout.labels.dueDate')}
            onChange={handleDueDateChange}
            isDisabled={!fieldEnabled('dueDate') || props.isEditingDisabled}
            isRequired={fieldRequired('dueDate')}
            data-testid={suffixTestId('dueDate', props)}
            isNonClearable
          />
        </Show>
        <Show when={fieldVisible('taxableEventDate')}>
          <FormField
            control={control}
            name="dateOfTaxableSupply"
            type="date"
            isRelativeDatesHidden
            label={i18n.t('entity.checkout.labels.taxableEventDate')}
            isDisabled={!fieldEnabled('taxableEventDate') || props.isEditingDisabled}
            isRequired={fieldRequired('taxableEventDate')}
            data-testid={suffixTestId('taxableEventDate', props)}
            maxDate={maxTaxableEventDate}
            minDate={minTaxableEventDate}
            isNonClearable
          />
        </Show>
        <Show when={fieldVisible('paymentMethod') && orderPaymentOptions.length > 0}>
          <GridItem span={1}>
            <Choice<AftersalesPaymentMethod>
              name="paymentMethod"
              value={paymentMethod}
              label={i18n.t('entity.checkout.labels.paymentMethod')}
              options={orderPaymentOptions}
              onChange={(value) => handlePaymentMethodChange(value)}
              isNotClearable
              isDisabled={!fieldEnabled('paymentMethod') || props.isEditingDisabled}
              data-testid={suffixTestId('paymentMethod', props)}
            />
          </GridItem>
        </Show>
        <Show when={fieldVisible('incomingBankAccount') && paymentMethod === 'BANK_TRANSFER'}>
          <GridItem span={1}>
            <FormField
              control={control}
              name="bankAccount"
              type="choice"
              options={bankAccountOptions}
              label={i18n.t('entity.checkout.labels.incomingBankAccount')}
              isRequired={fieldRequired('incomingBankAccount')}
              isNotClearable
              isDisabled={!fieldEnabled('incomingBankAccount') || props.isEditingDisabled}
              data-testid={suffixTestId('incomingBankAccount', props)}
            />
          </GridItem>
        </Show>
        <Show when={fieldVisible('cashRegisterId') && paymentMethod === 'CASH'}>
          <GridItem span={1}>
            <FormField
              control={control}
              name="cashRegisterId"
              type="choice"
              options={cashRegisterOptions}
              label={i18n.t('entity.checkout.labels.cashRegister')}
              isRequired={fieldRequired('cashRegisterId')}
              isNotClearable
              isDisabled={!fieldEnabled('cashRegisterId') || props.isEditingDisabled}
              data-testid={suffixTestId('cashRegisterId', props)}
            />
          </GridItem>
        </Show>
        <Show when={fieldVisible('customerServiceContractId')}>
          <GridItem span={1}>
            <FormField
              control={control}
              name="customerServiceContractId"
              type="choice"
              options={serviceContractOptions}
              label={i18n.t('entity.checkout.labels.customerServiceContract')}
              isRequired={fieldRequired('customerServiceContractId')}
              isDisabled={!fieldEnabled('customerServiceContractId') || props.isEditingDisabled}
              data-testid={suffixTestId('customerServiceContract', props)}
            />
          </GridItem>
        </Show>
      </Grid>
      <Show when={fieldVisible('note')}>
        <VStack spacing={1}>
          <HStack justify="space-between" align="flex-end">
            <Label>{i18n.t('general.labels.note')}</Label>
            <PredefinedNotes
              note={note ?? null}
              onPrefill={(prefillNote) => props.formApi.setValue('note', prefillNote)}
              resource="SERVICE_CASE"
              context="service_checkout"
              isLinkVariant
              isEditingDisabled={!fieldEnabled('note') || props.isEditingDisabled}
              data-testid={suffixTestId('predefinedNotes', props)}
            />
          </HStack>
          <FormField
            control={control}
            name="note"
            type="textarea"
            isDisabled={!fieldEnabled('note') || props.isEditingDisabled}
            data-testid={suffixTestId('note', props)}
          />
        </VStack>
      </Show>
      <Show when={isCorrectiveTaxDocument}>
        <Separator spacing={0} />
        <VStack spacing={4}>
          <Heading size={4}>
            {i18n.t('entity.correctiveTaxDocument.labels.taxDocumentDetails')}
          </Heading>
          <Grid spacing={4} columns={4}>
            <GridItem span={1}>
              <VStack spacing={1}>
                <Label>{i18n.t('entity.invoice.labels.invoiceNumberLabel')}</Label>
                <Text size="small" data-testid={suffixTestId('correctedInvoiceNumber', props)}>
                  {props.payment.invoiceNumber ?? EMPTY_PLACEHOLDER}
                </Text>
              </VStack>
            </GridItem>
            <GridItem span={3}>
              <VStack spacing={1}>
                <Label>{i18n.t('general.labels.description')}</Label>
                <Text size="small" data-testid={suffixTestId('correctionDescription', props)}>
                  {props.payment.description ?? EMPTY_PLACEHOLDER}
                </Text>
              </VStack>
            </GridItem>
            <Show when={fieldVisible('dateOfReception')}>
              <GridItem span={1}>
                <FormField
                  control={control}
                  name="dateOfReception"
                  type="date"
                  isRelativeDatesHidden
                  label={i18n.t('entity.correctiveTaxDocument.labels.dateOfReception')}
                  isRequired={fieldRequired('dateOfReception')}
                  isDisabled={!fieldEnabled('dateOfReception') || props.isEditingDisabled}
                  data-testid={suffixTestId('dateOfReception', props)}
                />
              </GridItem>
            </Show>
            <Show when={fieldVisible('correctionReason')}>
              <GridItem span={3}>
                <FormField
                  control={control}
                  name="correctionReason"
                  type="text"
                  label={i18n.t('general.labels.reason')}
                  isRequired={fieldRequired('correctionReason')}
                  isDisabled={!fieldEnabled('correctionReason') || props.isEditingDisabled}
                  data-testid={suffixTestId('correctionReason', props)}
                />
              </GridItem>
            </Show>
          </Grid>
        </VStack>
      </Show>
    </VStack>
  );
}
